import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PageHeader from "../Ui_Components/PageHeader";
import UserDetailSection from "../client_details/component/User_Details_Section/UserDetailSection";
import PlansTabNavigation from "./PlanPage_components/PlansTabNavigation";
import MealPlanTab from "./PlanPage_components/MealPlanTab";
import WaterPlanTab from "./PlanPage_components/WaterPlanTab";
import ExercisePlanTab from "./PlanPage_components/ExercisePlanTab";
import SupplementsPlanTab from "./PlanPage_components/SupplementsPlanTab";
import GuidelinesPlanTab from "./PlanPage_components/GuidelinesPlanTab";
import useCustomScrollbar from "../styles/useCustomScrollbar";
import { usePermissions } from "../../hooks/usePermissions";
import type { Resource } from "../../types/permissions";
import { Button } from "../Ui_Components/Button";

const TAB_RESOURCE_MAP: Array<{ id: string; resource: Resource }> = [
  { id: "meal",        resource: "meal_plan" },
  { id: "water",       resource: "water_plan" },
  { id: "exercise",    resource: "exercise_plan" },
  { id: "supplements", resource: "supplement_plan" },
  { id: "guidelines",  resource: "guideline" },
];

const PlansPage: React.FC = () => {
  useCustomScrollbar();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Read isDeactivated flag passed via navigation state from ClientPage
  const isDeactivated =
    (location.state as { isDeactivated?: boolean } | null)?.isDeactivated ===
    true;

  const { can } = usePermissions();
  // Pick the first tab the user can view as the initial active tab.
  const firstViewableTab =
    TAB_RESOURCE_MAP.find((t) => can(t.resource, "view"))?.id ?? "meal";
  const [activeTab, setActiveTab] = useState(firstViewableTab);
  const [isUserDetailExpanded, setIsUserDetailExpanded] = useState(false);

  // If permissions arrive after first render (e.g. via focus refresh) and the
  // current tab becomes unviewable, snap to the first viewable one.
  useEffect(() => {
    const current = TAB_RESOURCE_MAP.find((t) => t.id === activeTab);
    if (current && !can(current.resource, "view")) {
      const next = TAB_RESOURCE_MAP.find((t) => can(t.resource, "view"));
      if (next) setActiveTab(next.id);
    }
  }, [activeTab, can]);

  // Read the logged-in nutritionist's ID from storage
  // Adjust the key name to match whatever your auth flow saves
  const nutristId = parseInt(localStorage.getItem("nutrist_id") ?? "0", 10);

  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set([firstViewableTab]));
  const mealPlanTabRef = useRef<{ refresh: () => void } | null>(null);

  if (!userId) {
    return (
      <div className="min-h-screen bg-[#F7F7FA] flex items-center justify-center">
        <div className="text-center">
          <p className="text-base text-red-500 mb-2">
            No valid user ID found in URL
          </p>
          <p className="text-xs text-gray-500">
            Current path: {window.location.pathname}
          </p>
          <Button
            variant="primary"
            onClick={() => navigate("/client")}
            className="mt-4"
          >
            Go to Clients
          </Button>
        </div>
      </div>
    );
  }

  const userIdNumber = parseInt(userId, 10);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (!loadedTabs.has(tab)) {
      setLoadedTabs((prev) => {
        const newSet = new Set(prev);
        newSet.add(tab);
        return newSet;
      });
    }
  };

  return (
    <div className="h-screen flex flex-col bg-[#F7F7FA] overflow-hidden">
      <div className="flex-shrink-0">
        <PageHeader
          title="Plans"
          breadcrumb="Clients > Log details > Plans"
          breadcrumbLinks={["/client", `/client/${userId}`, ""]}
        />
      </div>

      <div
        className={`flex-1 flex flex-col ${
          isUserDetailExpanded
            ? "overflow-y-auto custom-scrollbar-overlay scrollbar-visible"
            : "overflow-hidden"
        }`}
      >
        <div
          className="flex flex-col"
          style={
            isUserDetailExpanded
              ? { minHeight: "100%" }
              : { flex: 1, minHeight: 0 }
          }
        >
          <div className="flex-shrink-0">
            <UserDetailSection
              userId={userIdNumber}
              onExpandChange={setIsUserDetailExpanded}
            />
          </div>

          <div className="flex-shrink-0 mb-4 px-4 sm:px-6 lg:px-8">
            <PlansTabNavigation
              activeTab={activeTab}
              setActiveTab={handleTabChange}
            />
          </div>

          <div
            className="px-4 sm:px-6 lg:px-8 pb-4"
            style={
              isUserDetailExpanded
                ? { height: "calc(100vh - 500px)", minHeight: "500px" }
                : { flex: 1, minHeight: 0 }
            }
          >
            {/* ── Meal Plan Tab ── */}
            {can("meal_plan", "view") && (
              <div
                style={{
                  display: activeTab === "meal" ? "block" : "none",
                  height: "100%",
                }}
              >
                <MealPlanTab
                  userId={userIdNumber}
                  ref={mealPlanTabRef}
                  isDeactivated={isDeactivated}
                />
              </div>
            )}

            {/* Water Plan Tab */}
            {can("water_plan", "view") && loadedTabs.has("water") && (
              <div
                style={{
                  display: activeTab === "water" ? "block" : "none",
                  height: "100%",
                }}
              >
                <WaterPlanTab
                  userId={userIdNumber}
                  nutristId={nutristId}
                  isDeactivated={isDeactivated}
                />
              </div>
            )}

            {/* Exercise Plan Tab */}
            {can("exercise_plan", "view") && loadedTabs.has("exercise") && (
              <div
                style={{
                  display: activeTab === "exercise" ? "block" : "none",
                  height: "100%",
                }}
              >
                <ExercisePlanTab
                  userId={userIdNumber}
                  isDeactivated={isDeactivated}
                />
              </div>
            )}

            {/* Supplements Plan Tab */}
            {can("supplement_plan", "view") && loadedTabs.has("supplements") && (
              <div
                style={{
                  display: activeTab === "supplements" ? "block" : "none",
                  height: "100%",
                }}
              >
                <SupplementsPlanTab
                  userId={userIdNumber}
                  isDeactivated={isDeactivated}
                />
              </div>
            )}

            {/* Guidelines Plan Tab */}
            {can("guideline", "view") && loadedTabs.has("guidelines") && (
              <div
                style={{
                  display: activeTab === "guidelines" ? "block" : "none",
                  height: "100%",
                }}
              >
                <GuidelinesPlanTab
                  userId={userIdNumber}
                  isDeactivated={isDeactivated}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlansPage;
